import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// __APP_VERSION__ is injected by Vite at build time (see vite.config.js).
// Every deploy produces a new value, and the same value is written to
// /version.json inside the image — so a mismatch between what this bundle
// was built with and what the server currently reports means a newer build
// has been deployed since this tab loaded the app.
//
// When a new version is found while the page is in use, it waits (owner,
// 3 Nov 2026: "why does the page keep refreshing? I lose all data"). Until
// then it reloaded within five minutes of every deploy, mid-typing. Now:
//   - on page load (nothing typed yet) it still updates at once;
//   - in use, it waits for the next move to another page, when what was on
//     screen is being left anyway, and offers "Update now" meanwhile;
//   - a page whose code no longer exists after a deploy (a failed download)
//     still reloads, because that page cannot open otherwise.
const CURRENT_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null;

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
// Remembers (per tab) which version we already reloaded for, so a cache that
// keeps serving the old index.html can never put the app into a reload loop.
const RELOAD_GUARD_KEY = 'parcelink_update_reloaded_for';

const fetchServerVersion = async () => {
    try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
            cache: 'no-store',
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        const data = await response.json();
        return typeof data?.version === 'string' ? data.version : null;
    } catch {
        return null;
    }
};

// The version waiting to be applied, shared with the watcher inside the router.
let pending = null;
const listeners = new Set();
const setPending = (v) => { pending = v; listeners.forEach((f) => f(v)); };
export const pendingUpdate = () => pending;

export function reloadFor(version, reload = () => window.location.reload()) {
    try {
        if (sessionStorage.getItem(RELOAD_GUARD_KEY) === version) return false;
        sessionStorage.setItem(RELOAD_GUARD_KEY, version);
    } catch {
        /* storage unavailable — still update, just without the guard */
    }
    reload();
    return true;
}

const AppUpdateGate = ({ currentVersion = CURRENT_VERSION, enabled = import.meta.env.PROD, getServerVersion = fetchServerVersion, reload }) => {
    const [ready, setReady] = useState(pending);
    const loaded = useRef(false);

    useEffect(() => {
        listeners.add(setReady);
        return () => { listeners.delete(setReady); };
    }, []);

    const check = useCallback(async ({ now = false } = {}) => {
        if (!currentVersion || !enabled) return;
        const server = await getServerVersion();
        if (!server || server === currentVersion) return;
        if (now) reloadFor(server, reload);
        else setPending(server);
    }, [currentVersion, enabled, getServerVersion, reload]);

    useEffect(() => {
        // Page load: nothing has been typed yet, so update straight away.
        if (!loaded.current) { loaded.current = true; check({ now: true }); }

        // In use: note a new version, apply it at the next page change.
        const onVisibilityChange = () => { if (document.visibilityState === 'visible') check(); };
        document.addEventListener('visibilitychange', onVisibilityChange);
        const interval = setInterval(() => check(), CHECK_INTERVAL_MS);

        // A page's code failed to download: it cannot open without a reload.
        const onPreloadError = () => check({ now: true });
        window.addEventListener('vite:preloadError', onPreloadError);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('vite:preloadError', onPreloadError);
            clearInterval(interval);
        };
    }, [check]);

    if (!ready) return null;
    return (
        <div role="status" className="fixed bottom-24 left-4 sm:bottom-4 z-[9999] flex items-center gap-3 rounded-full bg-slate-900 py-1.5 pl-4 pr-1.5 text-sm text-white shadow-lg">
            <span>A new version is ready</span>
            <button type="button" onClick={() => reloadFor(ready, reload)} className="rounded-full bg-white px-3 py-1 text-sm font-semibold text-slate-900 hover:bg-slate-100">
                Update now
            </button>
        </div>
    );
};

/** Inside the router: apply a waiting update when the user moves to another page. */
export function UpdateOnNavigate({ reload }) {
    const { pathname } = useLocation();
    const first = useRef(true);
    useEffect(() => {
        if (first.current) { first.current = false; return; }
        if (pending) reloadFor(pending, reload);
    }, [pathname, reload]);
    return null;
}

export default AppUpdateGate;
